import { ErrorHandler, Injectable, inject } from '@angular/core';

import { GfeLogService } from '@core/services/gfe-log/gfe-log.service';
import { GfeLogLevel } from '@core/services/gfe-log/gfe-log.types';

import { buildMetadata } from './build-metadata';
import { serializeError } from './serialize-error';

@Injectable()
export class GfeErrorHandler implements ErrorHandler {
  private readonly logService = inject(GfeLogService);

  handleError(error: unknown): void {
    try {
      this.logService.sendLog(
        GfeLogLevel.ERROR,
        serializeError(this.unwrap(error)),
        buildMetadata({ source: this.detectSource(error) })
      );
    } catch {
      // Never let logging errors propagate.
    }
    // Preserve the default DevEx: errors still surface in the console.
    console.error(error);
  }

  // Angular's provideBrowserGlobalErrorListeners() forwards window.onerror /
  // unhandledrejection events through this handler. The original error is
  // wrapped in an ErrorEvent / PromiseRejectionEvent — unwrap to keep the
  // serialized payload focused on the actual cause.
  private unwrap(error: unknown): unknown {
    if (typeof ErrorEvent !== 'undefined' && error instanceof ErrorEvent) {
      return error.error ?? error.message;
    }
    if (
      typeof PromiseRejectionEvent !== 'undefined' &&
      error instanceof PromiseRejectionEvent
    ) {
      return error.reason;
    }
    return error;
  }

  private detectSource(error: unknown): string {
    if (typeof ErrorEvent !== 'undefined' && error instanceof ErrorEvent) {
      return 'window.onerror';
    }
    if (
      typeof PromiseRejectionEvent !== 'undefined' &&
      error instanceof PromiseRejectionEvent
    ) {
      return 'unhandledrejection';
    }
    return 'angular-error-handler';
  }
}
